import { makeStyles, tokens } from '@coreai/fluentui-react';
import { ottoTokens } from '@/theme/ottoTokens';

export const useSettingsModalStyles = makeStyles({
  surface: {
    width: 'min(1080px, 100vw - 64px)',
    height: 'min(720px, 100vh - 64px)',
    maxWidth: 'none',
    padding: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    // NOTE: we deliberately do NOT set `willChange` / `contain` on the
    // surface. They were tempting because they smooth the modal's open
    // animation, but they break Fluent's portal-rendered popovers
    // (Dropdown listbox, Tooltip) — Floating UI computes the popover
    // anchor against the contained root and ping-pongs for a frame on
    // open, which reads as visible jitter. Pay the (small) one-time
    // open-animation cost to get correct popover behavior afterwards.
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    overflow: 'hidden',
  },
  side: {
    flexShrink: 0,
    width: '256px',
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke2,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // One horizontal rhythm for the whole panel: profile, search, and
    // nav items all align to the same left/right gutter. Sub-elements
    // override their own paddings to land on this rail.
    paddingInline: tokens.spacingHorizontalM,
  },
  profile: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingBlockStart: tokens.spacingVerticalL,
    paddingBlockEnd: tokens.spacingVerticalM,
    // Profile sits a hair inside so the avatar's optical center aligns
    // with the icon column of the nav items below.
    paddingInline: tokens.spacingHorizontalXS,
  },
  profileText: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  profileName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  profileHandle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Search spans the full panel gutter; its internal padding handles the
  // icon spacing, so the input visually matches the nav-item width.
  search: {
    paddingBlockEnd: tokens.spacingVerticalM,
  },
  searchInput: {
    width: '100%',
    // Hide the browser-native cancel button that ships with type="search".
    // Different browsers style it differently (blue × on Edge, gray on
    // Safari, hidden until focus on Chrome), which clashes with our own
    // DismissRegular clear button rendered via Input contentAfter. Same
    // approach as the Designer's CanvasSearch — our × is the single
    // source of truth for clearing.
    '& input::-webkit-search-cancel-button': {
      WebkitAppearance: 'none',
      display: 'none',
    },
    '& input::-webkit-search-decoration': {
      WebkitAppearance: 'none',
      display: 'none',
    },
  },
  /**
   * Inline clear button slotted into the Input's `contentAfter`. Sized
   * to match the search glyph on the left so the input reads as
   * visually symmetric, and tinted to the muted neutral so the × never
   * fights with whatever the user is typing.
   */
  searchClearButton: {
    minWidth: '20px',
    width: '20px',
    height: '20px',
    paddingInline: 0,
    color: tokens.colorNeutralForeground3,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  nav: {
    flex: 1,
    overflowY: 'auto',
    paddingBlockEnd: tokens.spacingVerticalM,
    // Structural negative inset: expands the nav hit/focus area back to
    // the panel rail. Negative-direction insets are not Fluent spacing tokens.
    marginInlineStart: '-4px',
    marginInlineEnd: '-4px',
    paddingInline: tokens.spacingHorizontalXS,
  },
  navGroup: {
    paddingInline: tokens.spacingHorizontalS,
    paddingBlockStart: tokens.spacingVerticalM,
    paddingBlockEnd: tokens.spacingVerticalXXS,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  navItem: {
    appearance: 'none',
    border: 0,
    background: 'transparent',
    width: '100%',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingInline: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground2,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    cursor: 'pointer',
    position: 'relative',
    transitionProperty: 'background-color, color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveDecelerateMax,
    ':hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
      color: tokens.colorNeutralForeground1,
    },
    ':active': {
      transform: 'scale(0.98)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': {
        transform: 'none',
      },
    },
  },
  navItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
      color: tokens.colorBrandForeground1,
    },
    // The rail indicator sits inside the item so it always lives within
    // the panel, even when the nav scrolls or the panel resizes. These
    // are structural pixel positions for the indicator, not spacing scale values.
    '::before': {
      content: '""',
      position: 'absolute',
      left: '2px',
      top: '7px',
      bottom: '7px',
      width: '3px',
      borderRadius: tokens.borderRadiusSmall,
      backgroundColor: tokens.colorBrandStroke1,
    },
  },
  navItemIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    display: 'inline-flex',
    // Drives the size of resizable icons (Fluent + CopilotLogoIcon use 1em)
    // so the whole Settings nav column renders at a consistent 18×18.
    fontSize: ottoTokens.settingsNavIconSize,
    // Optical bump: 18px icons sit ~1px low next to text caps.
    transform: 'translateY(-0.5px)',
  },
  navItemIconActive: {
    color: tokens.colorBrandForeground1,
  },
  navItemLabel: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  navItemBadge: {
    marginInlineStart: 'auto',
    fontSize: tokens.fontSizeBase100,
    paddingInline: tokens.spacingHorizontalXS,
    paddingBlock: '1px',
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground3,
    borderRadius: tokens.borderRadiusCircular,
    fontVariantNumeric: 'tabular-nums',
  },
  noResults: {
    paddingInline: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
    minWidth: 0,
  },
  contentHead: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingInline: tokens.spacingHorizontalXXL,
    paddingBlockStart: tokens.spacingVerticalL,
    paddingBlockEnd: tokens.spacingVerticalM,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
  },
  contentHeadTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  contentHeadTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textWrap: 'balance',
  },
  contentHeadCrumb: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBlockStart: tokens.spacingVerticalXXS,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    paddingBlockStart: tokens.spacingVerticalL,
    paddingBlockEnd: tokens.spacingVerticalXXXL,
  },
  footer: {
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  footerSpacer: {
    flex: 1,
  },
  kbd: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase100,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    // 5px is tuned to visually center single-character keyboard glyphs.
    paddingInline: '5px',
    paddingBlock: tokens.spacingVerticalXXS,
    marginInline: tokens.spacingHorizontalXXS,
  },
});
